import { useCallback } from "react";

import Box from "../Box";
import DeltaBadge from "../DeltaBadge";
import styled from "styled-components";
import MrclText from "../marcel/MrclText";
import BaseDashboardCard from "../BaseDashboardCard";
import useBreakPoint from "../../utils/useBreakPoint";
import SubTornover from "../SubTurnover/SubTurnover";
import { CommercialUnitData, TurnoverObjective } from "../../types/commercial";
import { DriveObjective, DriveTurnover } from "../../types/drive";
import { add, computeRHRate } from "../../utils/turnover";
import { useTranslation } from "react-i18next";
import { formatKvalue, getCurrencySuffix } from "../../utils/numberUtils";
import { COLORS } from "../../utils/colors";

type TurnoverCardProps = {
  currentTurnoverData: CommercialUnitData;
  pastTurnoverData: CommercialUnitData;
  currentDriveData: DriveTurnover;
  pastDriveData: DriveTurnover;
  turnoverObjectiveData: TurnoverObjective;
  driveObjective: DriveObjective;
  isUnavailable: boolean;
  isLoading: boolean;
  openInformationModal: (title?: string) => void;
};

const Text = styled(MrclText)`
  font-family: system-ui;
`;
const CTextCustum = styled(MrclText)<{ $color: string }>`
  font-family: system-ui;
  color: ${(props) => props.$color} !important;
`;
const BoxCustom = styled(Box)<{ gap: string }>`
  gap: ${(props) => props.gap};
  display: flex;
`;

const TurnoverCard = ({
  currentTurnoverData,
  currentDriveData,
  pastDriveData,
  pastTurnoverData,
  driveObjective,
  turnoverObjectiveData,
  ...res
}: TurnoverCardProps) => {

  const isBreak = useBreakPoint();
  const totalCurrentTurnover = add(
    (currentTurnoverData?.turnover as number) || 0,
    (currentDriveData?.turnover as number) || 0
  );
  const totalPastTurnover = add(
    (pastTurnoverData?.turnover as number) || 0,
    (pastDriveData?.turnover as number) || 0
  );
  const totalObjectiveTurnover = add(
    (turnoverObjectiveData?.objectiveAmount as number) || 0,
    (driveObjective?.objectiveAmount as number) || 0
  );
  const totalRHRate =
    computeRHRate(totalCurrentTurnover, totalPastTurnover) || null;
  const turnoverSdvRHRate = computeRHRate(
    currentTurnoverData?.turnover as number,
    pastTurnoverData?.turnover as number
  );
  const driveRHRate = computeRHRate(
    currentDriveData?.turnover as number,
    pastDriveData?.turnover as number
  );
  
  const diff =
    totalCurrentTurnover === 0 && totalObjectiveTurnover === 0
      ? null
      : totalCurrentTurnover - totalObjectiveTurnover;

  const { t } = useTranslation();

  const objectiveSection = useCallback(() => 
              <Box display={"flex"} alignItems={"center"} py={1}>
              {diff ? (
                <CTextCustum
                  $color={diff > 0 ? COLORS.green : COLORS.red}
                  bold
                  tagStyle="p"
                  size={"s"}
                >
                  {`${
                    diff > 0
                      ? `+${formatKvalue(diff, "")}`
                      : formatKvalue(diff, "")
                    }`}
                    {getCurrencySuffix(diff)}
                </CTextCustum>
              ) : (
                "-"
              )}
              <Text size={"s"} tagStyle="p" style={{ paddingInline: "4px" }}>
                {t('common.sur_objectif_de')}
              </Text>
              {diff ? (
                <Text bold size={"s"} tagStyle="p">
                  {formatKvalue(totalObjectiveTurnover, "")}
                  {getCurrencySuffix(totalObjectiveTurnover)}
                  {diff > 0 ? " 🎉" : null}
                </Text>
              ) : (
                "-"
              )}
              </Box>, [totalObjectiveTurnover, diff, t]);
  return (
    <BaseDashboardCard
      hasModal={true}
      title={t('commercial.chiffre_affaires')}
      size={"big"}
      importedDateTime={currentDriveData?.importedDateTime || currentTurnoverData?.importedDateTime}
      {...res}
    >
      <Box display={"flex"} justifyContent={"center"} justifyItems={"center"}>
        <BoxCustom
          gap={`${isBreak === "phone" ? "12px" : "2rem"}`}
          flexDirection={["column", "row"]}
          width="100%"
          borderRadius={1}
          pt={[1, 4]}
        >
          {/* CA */}
          <Box
            borderRadius={1}
            border={"1px solid #D9D9D9"}
            display={"flex"}
            flexDirection={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            p={2}
            width={["100%", "50%"]}
          >
            <Box display={"flex"} alignItems={"end"} pb={1}>
              {totalCurrentTurnover && totalCurrentTurnover > 0 ? (
                <Text tagStyle="display">{`${formatKvalue(totalCurrentTurnover,"")}`}</Text>
              ) : (
                "-"
              )}
              {totalCurrentTurnover && totalCurrentTurnover > 0 ? (
                <Text tagStyle="subtitle">{`${
                  getCurrencySuffix(totalCurrentTurnover)
                }`}</Text>
              ) : null}
            </Box>
            {totalRHRate ? (
              <BoxCustom gap="4px" display={"flex"} alignItems={"center"}>
                <DeltaBadge value={totalRHRate} />
                <Text tagStyle="caption">{"vs "} {t('commercial.n_1')}</Text>
              </BoxCustom>
            ) : (
              "-"
            )}
            {!!totalObjectiveTurnover && objectiveSection()}
          </Box>
          <Box width={["100%", "50%"]}>
            {/* CA SDV  */}
            <SubTornover
              title={t('commercial.sdv')}
              target={turnoverObjectiveData?.objectiveAmount}
              value={currentTurnoverData?.turnover}
              rate={turnoverSdvRHRate as number}
            />
            {/* CA Drive  */}
            <SubTornover
              title={t('commercial.ecommerce')}
              target={driveObjective?.objectiveAmount}
              value={currentDriveData?.turnover}
              rate={driveRHRate as number}
            />
          </Box>
        </BoxCustom>
      </Box>
    </BaseDashboardCard>
  );
};

export default TurnoverCard;
